import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { capitalize } from 'webapp/utils/common';
import { getPrettyModelName } from 'webapp/utils/model';

import joinClasses from 'webapp/helpers/join-classes';
import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { Board, BoardLoop, Goal, Report } from 'webapp/models-custom';
import Space from 'webapp/models-custom/space';
import type UserRecord from 'webapp/models/user';

interface Args {
	model: Report | Board | BoardLoop | Goal | Space;
	canEdit?: boolean;
	isOwnerNameHidden?: boolean;
}

export default class OwnerSelectComponent extends Component<Args> {
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('request') declare requestService: Service.Request;
	@service('account') declare accountService: Service.Account;

	selectOwnerText = 'Select owner';

	get modelTypeText(): string {
		return getPrettyModelName(this.args.model);
	}

	get ownerUserRecord(): UserRecord | null {
		const model = this.args.model;
		let ownerUserRecord;

		if (model instanceof Space) {
			ownerUserRecord = model.accountManager;
		} else {
			ownerUserRecord = model.ownerUser;
		}

		return ownerUserRecord || null;
	}

	@action
	changeOwner(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (!this.args.canEdit) {
			return;
		}

		const modelTypeText = this.modelTypeText;

		this.mmUsersService.openUserSelectPopup({
			title: `${capitalize(modelTypeText)} owner`,
			text: `Select an owner for a ${modelTypeText}:`,
			currentUserId: String(this.args.model.ownerId),
			onSelect: (userId: string | null) => {
				const ownerId = userId ? +userId : null;
				this.args.model.ownerId = ownerId;
				this.args.model
					.save()
					.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
			},
		});
	}
	<template>
		<div
			role="button"
			class={{joinClasses
				(array
					"owner-select"
					(if @canEdit "owner-select--can-edit")
					(unless this.ownerUserRecord "owner-select--empty")
				)
			}}
			{{on "click" this.changeOwner}}
		>
			{{#if this.ownerUserRecord}}
				<AvatarOrInitialsComponent
					@name={{this.ownerUserRecord.name}}
					@avatar={{this.ownerUserRecord.avatar}}
				/>

				{{#if @isOwnerNameHidden}}
					<ToolTipComponent @text={{this.ownerUserRecord.name}} />
				{{else}}
					<span>{{this.ownerUserRecord.name}}</span>
				{{/if}}
			{{else}}
				{{#if @isOwnerNameHidden}}
					<ToolTipComponent @text={{this.selectOwnerText}} />
				{{else}}
					<span>{{this.selectOwnerText}}</span>
				{{/if}}
			{{/if}}
		</div>
	</template>
}
